import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { formatFeedClock, useFeedHealth } from '@/lib/useFeedHealth';

/**
 * When MT5 last wrote a price, shown on the dashboard.
 *
 * The feed going quiet is invisible otherwise: every chart keeps rendering the
 * last bars it has, so a disconnected terminal looks exactly like a slow
 * market. That is how a month-long outage went unnoticed.
 */
export function FeedStatus() {
  const { t } = useI18n();
  const { state, updatedAt, age } = useFeedHealth();
  const live = state === 'live';

  return (
    <div
      className="glass flex items-center gap-2 rounded-xl px-3 py-2 text-xs"
      title={t('feed.tooltip')}
    >
      <span className="relative flex size-2">
        {live && (
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-bull opacity-60" />
        )}
        <span className={cn('relative inline-flex size-2 rounded-full', live ? 'bg-bull' : 'bg-danger')} />
      </span>
      <span className="text-muted-foreground">{t('feed.label')}</span>
      {updatedAt === null ? (
        <span className="font-medium text-danger">
          {state === 'loading' ? t('common.loading') : t('feed.unknown')}
        </span>
      ) : (
        <>
          <span className="tabular font-medium">{formatFeedClock(updatedAt)}</span>
          <span className={cn('tabular', live ? 'text-muted-foreground' : 'font-medium text-danger')}>
            {formatAge(age!, t)}
          </span>
        </>
      )}
    </div>
  );
}

export function formatAge(sec: number, t: ReturnType<typeof useI18n>['t']): string {
  const s = Math.floor(sec);
  if (s < 60) return t('feed.secondsAgo', { n: s });
  if (s < 3600) return t('feed.minutesAgo', { n: Math.floor(s / 60) });
  if (s < 86400) return t('feed.hoursAgo', { n: Math.floor(s / 3600) });
  return t('feed.daysAgo', { n: Math.floor(s / 86400) });
}
